<template>
  <v-form ref="form" v-model:model-value="validated" :disabled="loader.loading">
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="model.title"
          density="compact"
          variant="outlined"
          label="Titulo *"
          :rules="[rules.required]"
          hide-details
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="model.description"
          density="compact"
          variant="outlined"
          label="Descrição"
          hide-details
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="3">
        <date-time-field
          v-model="model.publish_at"
          label="Data de Publicação"
          dense="compact"
          :loading="loader.loading"
          :rules="[rules.required]"
        />
      </v-col>
      <v-col cols="12" md="3">
        <date-time-field
          v-model="model.unpublish_at"
          label="Data de Despublicação *"
          dense="compact"
          :loading="loader.loading"
          :rules="[rules.required]"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="model.platform"
          density="compact"
          :items="platforms"
          item-text="title"
          item-value="value"
          label="Plataforma *"
          variant="outlined"
          :rules="[rules.required]"
          hide-details
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="model.status"
          density="compact"
          :items="statuses"
          item-text="title"
          item-value="value"
          label="Status *"
          variant="outlined"
          :rules="[rules.required]"
          hide-details
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="3">
        <v-autocomplete
          v-model="model.producer_id"
          density="compact"
          item-text="title"
          item-value="value"
          :disabled="!producersFormatted.length"
          :items="producersFormatted"
          label="Produtor"
          variant="outlined"
          hide-details
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="model.category"
          density="compact"
          :items="categories"
          item-text="title"
          item-value="value"
          label="Categoria *"
          variant="outlined"
          :rules="[rules.required]"
          hide-details
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="model.link"
          density="compact"
          variant="outlined"
          label="Link *"
          :rules="[rules.url]"
          hide-details
        />
      </v-col>
      <v-col cols="12" md="3">
        <image-file-picker
          v-model="image"
          precise
          :url="model.url"
          :dimensions="dimensions"
          :label="`Banner ${model.platform}`"
        />
      </v-col>
    </v-row>

    <v-row justify="center">
      <v-dialog
        v-model="onPreview"
        scrollable
        max-width="650px"
      >
        <v-card>
          <v-toolbar
            color="primary"
            dark
            class="pl-6"
          >
            <span class="text-h6 text-center">
              Preview da Imagem
            </span>
          </v-toolbar>
          <v-card-text>
            <v-row v-if="model.platform === 'desktop'">
              <v-col cols="12">
                <div class="d-flex">
                  <v-img :src="imagePreview(image as File, model.url as string)" height="100%" />
                </div>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12">
                <div class="d-flex">
                  <v-img :src="imagePreview(image as File, model.url as string)" height="100%" />
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>

    <v-row justify="end">
      <v-col cols="auto">
        <v-btn
          color="secondary"
          block
          :loading="loader.loading"
          :disabled="!hasImages"
          size="large"
          @click="onPreview = !onPreview"
        >
          Ver Imagem
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="primary"
          block
          :loading="loader.loading"
          size="large"
          @click="submit"
        >
          {{ model.id == undefined ? 'Criar' : 'Salvar' }}
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script lang="ts" setup>
import { ref, Ref, computed } from 'vue'
import ImageFilePicker from '@/components/form/ImageFilePicker.vue'
import DateTimeField from '@/components/form/DateTimeField.vue'

import { Banner } from '@/types/Banner'
import type SelectItem from '@/types/vuetify/SelectItem'
import { useProducerStore } from '@/stores/producer'
import { useSnackBar } from '@/stores/snackbar'
import { onMounted } from 'vue'
import { PlatformDimensions } from '@/constants/Banners'
import { ValidationRule } from '@/types/vuetify/ValidationRule'
import { useLoader } from '@/stores/loader'

const loader = useLoader()
const snackbar = useSnackBar()

interface Props {
  banner: Banner
}

const { banner } = defineProps<Props>()
const model: Ref<Banner> = ref(banner)

const emit = defineEmits< { submit: [ model: Banner, images?: File ] }>()

const producersFormatted = computed(() => useProducerStore().producers.map(
  el => ({
    title: el.data.trade_name,
    value: el.id,
  }),
))

const image = ref<File | undefined>()

const statuses: SelectItem<string>[] = [
  { title: 'Ativo', value: 'active' },
  { title: 'Inativo', value: 'inactive' },
]

const platforms: SelectItem<string>[] = [
  { title: 'Desktop', value: 'desktop' },
  { title: 'Mobile', value: 'mobile' },
]

const categories: SelectItem<string>[] = [
  { title: 'Interno', value: 'internal' },
  { title: 'Externo', value: 'external' },
]

const rules = {
  required: (v: string) => (!!v || v == String(false)) || 'Campo obrigatório',
  url: (v?: string) => {
    if (!v?.trim()) return true
    const pattern = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(\/\S*)?$/i
    return pattern.test(v) || 'URL inválida'
  },
} as Record<string, ValidationRule>

const validated = ref(true)

const onPreview = ref(false)

const dimensions = computed(() => PlatformDimensions[model.value.platform])

const hasImages = computed(() => {
  return !!image.value || !!model.value.url
})

function imagePreview(file: File | undefined, url: string | undefined): string {
  if (file) return URL.createObjectURL(file)

  if (url) {
    const pattern = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(\/\S*)?$/i
    if (pattern.test(url)) return url
  }

  return ''
}

function submit() {
  if (!validated.value) {
    snackbar.show(
      'Verifique se todos os campos foram preenchidos corretamente',
      'warning',
    )
    return
  }

  emit('submit', model.value, image.value)
}

onMounted(() => {
  if (!['mobile', 'desktop'].includes(model.value.platform)) {
    model.value.platform = 'desktop'
  }
})

</script>
